import React, { createContext, useContext, useReducer, useEffect } from "react";
import { Navigate } from "react-router-dom";
import api from "../services/api";
import toast from "react-hot-toast";

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const actionTypes = {
  SET_LOADING: "SET_LOADING",
  SET_USER: "SET_USER",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  LOGOUT: "LOGOUT",
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case actionTypes.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null,
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case actionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case actionTypes.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth Provider Component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Get stored token
  const getStoredToken = () => {
    return localStorage.getItem("kslnk_token");
  };

  // Store token
  const storeToken = (token) => {
    localStorage.setItem("kslnk_token", token);
  };

  // Remove token
  const removeToken = () => {
    localStorage.removeItem("kslnk_token");
  };

  // Set auth header for API calls
  const setAuthHeader = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };

  // Load user from token
  const loadUser = async () => {
    const token = getStoredToken();

    if (!token) {
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
      return;
    }

    try {
      setAuthHeader(token);
      const response = await api.get("/auth/me");

      if (response.data.success) {
        dispatch({ type: actionTypes.SET_USER, payload: response.data.data });
      } else {
        throw new Error("Invalid token");
      }
    } catch (error) {
      console.error("Load user error:", error);
      removeToken();
      setAuthHeader(null);
      dispatch({ type: actionTypes.LOGOUT });
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      dispatch({ type: actionTypes.CLEAR_ERROR });

      const response = await api.post("/auth/login", {
        email,
        password,
      });

      if (response.data.success) {
        const { user, token } = response.data.data;

        storeToken(token);
        setAuthHeader(token);
        dispatch({ type: actionTypes.SET_USER, payload: user });

        toast.success(`Hoş geldiniz, ${user.firstName}!`);
        return { success: true, user };
      } else {
        throw new Error(response.data.error?.message || "Giriş başarısız");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Giriş yapılırken bir hata oluştu";
      dispatch({ type: actionTypes.SET_ERROR, payload: errorMessage });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      dispatch({ type: actionTypes.CLEAR_ERROR });

      const response = await api.post("/auth/register", userData);

      if (response.data.success) {
        const { user, token } = response.data.data;

        storeToken(token);
        setAuthHeader(token);
        dispatch({ type: actionTypes.SET_USER, payload: user });

        toast.success(`Hesabınız oluşturuldu, hoş geldiniz ${user.firstName}!`);
        return { success: true, user };
      } else {
        throw new Error(response.data.error?.message || "Kayıt başarısız");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Kayıt olurken bir hata oluştu";
      dispatch({ type: actionTypes.SET_ERROR, payload: errorMessage });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint (optional, since JWT is stateless)
      await api.post("/auth/logout").catch(() => {
        // Ignore logout endpoint errors
      });
    } catch (error) {
      console.log("Logout endpoint error:", error);
    } finally {
      // Always clear local state and token
      removeToken();
      setAuthHeader(null);
      dispatch({ type: actionTypes.LOGOUT });
      toast.success("Güvenli bir şekilde çıkış yaptınız");
    }
  };

  // Update profile function
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put("/auth/profile", profileData);

      if (response.data.success) {
        const updatedUser = { ...state.user, ...response.data.data };
        dispatch({ type: actionTypes.SET_USER, payload: updatedUser });
        toast.success("Profil başarıyla güncellendi");
        return { success: true, user: updatedUser };
      } else {
        throw new Error(
          response.data.error?.message || "Profil güncellenemedi"
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Profil güncellenirken bir hata oluştu";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!state.user) return false;

    switch (permission) {
      case "admin":
        return state.user.role === "admin";
      case "user":
        return state.user.role === "user" || state.user.role === "admin";
      default:
        return false;
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return state.user?.role === "admin";
  };

  // Check if user is regular user
  const isUser = () => {
    return state.user?.role === "user";
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: actionTypes.CLEAR_ERROR });
  };

  // Effect to load user on app startup
  useEffect(() => {
    loadUser();
  }, []);

  // Context value
  const value = {
    // State
    ...state,

    // Actions
    login,
    register,
    logout,
    updateProfile,
    clearError,
    loadUser,

    // Helpers
    hasPermission,
    isAdmin,
    isUser,

    // Token helpers (for external use if needed)
    getStoredToken,
    storeToken,
    removeToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

// Higher-order component for authentication
export function withAuth(Component) {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="loading-spinner w-8 h-8"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    return <Component {...props} />;
  };
}

// Higher-order component for admin routes
export function withAdminAuth(Component) {
  return function AdminAuthenticatedComponent(props) {
    const { isAuthenticated, isAdmin, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="loading-spinner w-8 h-8"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (!isAdmin()) {
      return <Navigate to="/unauthorized" replace />;
    }

    return <Component {...props} />;
  };
}

export default AuthContext;
